import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, ButtonProps } from './ui/button';
import { Sparkles, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRealm } from '../context/RealmContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useTranslation } from 'react-i18next';

interface PortalButtonProps extends Omit<ButtonProps, 'onClick'> {
  children: React.ReactNode;
  showTooltip?: boolean;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  tooltipContent?: React.ReactNode;
}

export default function PortalButton({ 
  children, 
  className,
  size = "default",
  showTooltip = true,
  tooltipSide = 'right',
  tooltipContent,
  ...props 
}: PortalButtonProps) {
  const { isTransitioning, currentRealm, enterWorkspace, exitWorkspace } = useRealm();
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleClick = () => {
    if (isTransitioning) return;
    
    // Use realm context functions for proper transitions (SPA; preserves auth state)
    if (currentRealm === 'outer') {
      enterWorkspace();
    } else {
      exitWorkspace();
    }
  };

  const getButtonContent = () => (
    <span className="flex items-center gap-2">
      {children}
      <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
    </span>
  );
  
  // Get the default tooltip content based on realm
  const getDefaultTooltipContent = () => {
    if (currentRealm === 'outer') {
      return (
        <div className="max-w-xs">
          <h4 className="font-medium mb-1">{t('transitions.enterMemoryWorkspace', { ns: 'navigation' }) as string}</h4>
          <p className="text-sm opacity-90">{t('transitions.enterMemoryWorkspaceDesc', { ns: 'navigation' }) as string}</p>
        </div>
      );
    } else {
      return (
        <div className="max-w-xs">
          <h4 className="font-medium mb-1">{t('transitions.returnToDashboard', { ns: 'navigation' }) as string}</h4>
          <p className="text-sm opacity-90">{t('transitions.returnToDashboardDesc', { ns: 'navigation' }) as string}</p>
        </div>
      );
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={showTooltip ? 300 : 999999}>
        <TooltipTrigger asChild>
          <motion.div
            whileHover={prefersReducedMotion ? undefined : { scale: 1.02 }}
            whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
            className="relative"
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
          >
            <Button
              onClick={handleClick}
              disabled={isTransitioning}
              size={size}
              className={cn(
                "relative overflow-hidden group transition-all duration-300",
                "hover:shadow-lg",
                currentRealm === 'outer' ? "hover:ring-2 hover:ring-indigo-400/50" : "hover:ring-2 hover:ring-purple-400/50",
                className
              )}
              {...props}
            >
              {/* Improved background gradient animation */}
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    className={`absolute inset-0 ${currentRealm === 'outer' 
                      ? "bg-gradient-to-r from-indigo-600/80 via-purple-600/60 to-indigo-600/80" 
                      : "bg-gradient-to-r from-purple-600/80 via-indigo-600/60 to-purple-600/80"}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      backgroundSize: '200% 100%',
                      ...(prefersReducedMotion
                        ? {}
                        : { animation: 'shimmer 2s infinite linear' }),
                    }}
                  />
                )}
              </AnimatePresence>
              
              {/* Portal icon indicator */}
              {isHovered && !isTransitioning && (
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.08 }}
                  transition={{ duration: 0.3 }}
                >
                  <Brain className={`w-24 h-24 text-${currentRealm === 'outer' ? 'white' : 'white'}`} />
                </motion.div>
              )}
              
              {/* Content */}
              <span className="relative z-10 flex items-center">
                {isTransitioning ? (
                  <motion.div className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                    />
                    <span>{t('transitions.openingPortal', { ns: 'navigation' }) as string}</span>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={false}
                    animate={{
                      x:
                        isHovered && !prefersReducedMotion ? [0, 5, 0] : 0,
                    }}
                    transition={{
                      duration: 0.5,
                      ease: 'easeInOut',
                    }}
                  >
                    {getButtonContent()}
                  </motion.div>
                )}
              </span>
            </Button>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>
          {tooltipContent || getDefaultTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
} 